// Push Notification Service for PWA
import { getAuthToken } from '../api/axiosConnector';

// You'll need to replace this with your actual VAPID public key
// Generate VAPID keys using: npx web-push generate-vapid-keys
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'BEKynWawqlF-kZ2_ovgIZZhx6BRpXtFChpA2OeOW5PQ7vcG6ZcpsrGV_zxgNRXoxjFcGD9iPt1cUd5pbeOgx1lE';

export interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  userId?: string;
}

class PushNotificationService {
  private isSupported: boolean;
  private subscription: globalThis.PushSubscription | null = null;

  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Check if push notifications are supported
  isNotificationSupported(): boolean {
    return this.isSupported;
  }

  // Check current permission status
  getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      console.warn('Notifications are blocked by the user');
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  }

  // Convert VAPID key for subscription
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Subscribe user to push notifications
  async subscribeToPush(): Promise<globalThis.PushSubscription | null> {
    if (!this.isSupported) {
      console.warn('Push notifications are not supported');
      return null;
    }

    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      console.warn('Permission not granted for notifications');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('Already subscribed to push notifications');
        this.subscription = existingSubscription;
        return existingSubscription;
      }

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      console.log('Successfully subscribed to push notifications:', subscription);
      this.subscription = subscription;
      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribeFromPush(): Promise<boolean> {
    if (!this.isSupported) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        const result = await subscription.unsubscribe();
        console.log('Unsubscribed from push notifications:', result);
        this.subscription = null;
        return result;
      }
      
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  // Get current subscription
  async getCurrentSubscription(): Promise<globalThis.PushSubscription | null> {
    if (!this.isSupported) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      this.subscription = subscription;
      return subscription;
    } catch (error) {
      console.error('Failed to get current subscription:', error);
      return null;
    }
  }

  // Convert subscription to JSON for sending to server
  subscriptionToJSON(subscription: globalThis.PushSubscription): PushSubscription {
    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...Array.from(new Uint8Array(subscription.getKey('p256dh')!)))),
        auth: btoa(String.fromCharCode(...Array.from(new Uint8Array(subscription.getKey('auth')!))))
      }
    };
  }

  // Send subscription to backend
  async sendSubscriptionToBackend(subscription: globalThis.PushSubscription, userId: string): Promise<boolean> {
    try {
      const subscriptionData = this.subscriptionToJSON(subscription);
      subscriptionData.userId = userId;

      const apiUrl = `${process.env.REACT_APP_API_HOST || window.location.origin}/push-notifications/subscribe`;
      console.log('🔗 FRONTEND: Sending subscription to:', apiUrl);
      console.log('📝 FRONTEND: Subscription data:', { ...subscriptionData, keys: '***hidden***' });

      // Replace with your actual API endpoint
      const token = getAuthToken();
      console.log('🔗 FRONTEND: Token:', token ? 'Present' : 'Missing');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(subscriptionData)
      });

      console.log('📬 FRONTEND: Response status:', response.status, response.statusText);
      console.log('📬 FRONTEND: Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('📬 FRONTEND: Response data:', responseData);
        console.log('Subscription sent to backend successfully');
        return true;
      } else {
        const errorText = await response.text();
        console.error('Failed to send subscription to backend:', response.status, response.statusText);
        console.error('Error response body:', errorText);
        return false;
      }
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      return false;
    }
  }

  // Remove subscription from backend
  async removeSubscriptionFromBackend(userId: string): Promise<boolean> {
    try {
      const response = await fetch('/push-notifications/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        console.log('Subscription removed from backend successfully');
        return true;
      } else {
        console.error('Failed to remove subscription from backend:', response.statusText);
        return false;
      }
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
      return false;
    }
  }

  // Complete subscription process (subscribe + send to backend)
  async enablePushNotifications(userId: string): Promise<boolean> {
    try {
      const subscription = await this.subscribeToPush();
      if (!subscription) {
        return false;
      }

      const backendResult = await this.sendSubscriptionToBackend(subscription, userId);
      return backendResult;
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      return false;
    }
  }

  // Complete unsubscription process (unsubscribe + remove from backend)
  async disablePushNotifications(userId: string): Promise<boolean> {
    try {
      const [unsubscribeResult, backendResult] = await Promise.all([
        this.unsubscribeFromPush(),
        this.removeSubscriptionFromBackend(userId)
      ]);

      return unsubscribeResult && backendResult;
    } catch (error) {
      console.error('Error disabling push notifications:', error);
      return false;
    }
  }

  // Show a test notification (for testing purposes)
  async showTestNotification(): Promise<void> {
    const permission = await this.requestPermission();
    if (permission === 'granted') {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification('Teste de Notificação', {
          body: 'As notificações estão funcionando corretamente!',
          icon: '/manu/manu-icon-192.png',
          badge: '/manu/favicon.ico',
          tag: 'test-notification',
          requireInteraction: true,
          silent: false,
          vibrate: [200, 100, 200],
          actions: [
            {
              action: 'view',
              title: 'Ver',
              icon: '/manu/manu-icon-192.png'
            },
            {
              action: 'dismiss',
              title: 'Dispensar'
            }
          ]
        });
        console.log('Test notification shown successfully');
      } catch (error) {
        console.error('Error showing test notification via service worker:', error);
        // Fallback to basic notification if service worker fails
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Teste de Notificação', {
            body: 'As notificações estão funcionando corretamente!',
            icon: '/manu/manu-icon-192.png',
            badge: '/manu/favicon.ico',
            tag: 'test-notification'
          });
        }
      }
    } else {
      throw new Error('Notification permission not granted');
    }
  }

  // Check if user is currently subscribed
  async isSubscribed(): Promise<boolean> {
    const subscription = await this.getCurrentSubscription();
    return subscription !== null;
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();